import type { SupportCompanyDetail } from '@siteday/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { errorMessage } from '@/api/client';
import * as supportApi from '@/api/support';
import { qk } from '@/api/support';
import { ErrorState, LoadingState, PageHeader } from '@/components/states';
import { Pill, type PillTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, EmptyRow, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { Switch } from '@/components/ui/toggle';
import { fmtAgo, fmtDate, fmtDateTime } from '@/lib/format';

import { adoptionPill } from './adoption';

export function CompanyDetailPage() {
  const { id = '' } = useParams();
  const company = useQuery({ queryKey: qk.company(id), queryFn: () => supportApi.getCompany(id), enabled: id.length > 0 });
  if (company.isPending) return <LoadingState />;
  if (company.isError) return <ErrorState error={company.error} onRetry={() => void company.refetch()} />;
  return <Detail c={company.data} />;
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string | undefined }) {
  return (
    <div className="rounded-xl border border-line2 bg-surface px-4 py-3 shadow-card">
      <div className="eyebrow">{label}</div>
      <div className="mt-1 font-cond text-[22px] font-extrabold num">{value}</div>
      {sub ? <div className="text-[11px] text-ink3">{sub}</div> : null}
    </div>
  );
}

const roleTone: Record<string, PillTone> = { owner: 'orange', admin: 'info', foreman: 'good', worker: 'neutral' };
const statusTone: Record<string, PillTone> = { active: 'good', bidding: 'info', complete: 'neutral', ok: 'good', success: 'good', running: 'info', failed: 'bad', partial: 'warn' };

function Detail({ c }: { c: SupportCompanyDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const pill = adoptionPill(c.adoption);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.company(c.id) });
    void qc.invalidateQueries({ queryKey: qk.companies() });
  };
  const access = useMutation({
    mutationFn: (allowed: boolean) => supportApi.patchCompany(c.id, { supportAccessAllowed: allowed }),
    onSuccess: (row) => {
      invalidate();
      toast.success(row.supportAccessAllowed ? 'Support access allowed' : 'Support access revoked');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const open = useMutation({
    mutationFn: () => supportApi.impersonate(c.id),
    onSuccess: (r) => {
      // The code lives 60 s and burns on first use; the office app trades it for a read-only session.
      const tab = window.open(r.handoffUrl, '_blank', 'noopener');
      if (!tab) toast.info('Pop-up blocked — allow pop-ups for this site and try again');
      else toast.success('Opened read-only in a new tab');
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <Link to="/" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-ink3 hover:text-ink">
        <ArrowLeft className="size-3.5" /> All companies
      </Link>
      <PageHeader
        title={c.name}
        sub={
          <span className="flex flex-wrap items-center gap-2">
            <Pill tone={pill.tone} title={pill.hint}>
              {pill.label}
            </Pill>
            <span>
              {c.timezone} · plan {c.plan} · created {fmtDate(c.createdAt.slice(0, 10))}
            </span>
            <span className="text-ink3">·</span>
            <code className="text-[11px] text-ink3">{c.id}</code>
          </span>
        }
        actions={
          <Button
            variant="primary"
            onClick={() => open.mutate()}
            loading={open.isPending}
            disabled={!c.supportAccessAllowed}
            title={c.supportAccessAllowed ? 'Opens the office app as the owner, read-only, in a new tab (logged)' : 'The owner has revoked support access'}
            data-testid="open-read-only"
          >
            <ExternalLink /> Open read-only
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Stat label="Entries 7d" value={c.entriesLast7} />
        <Stat label="Entries 30d" value={c.entriesLast30} />
        <Stat label="Last entry" value={fmtDate(c.lastEntryDate)} />
        <Stat label="Foremen" value={c.counts.foremen} sub={`${c.counts.owners} owner · ${c.counts.admins} admin · ${c.counts.workers} workers`} />
        <Stat label="Active jobs" value={c.counts.activeJobs} />
        <Stat label="QuickBooks" value={c.qboConnected ? 'Connected' : 'Not connected'} />
      </div>

      <Card className="mb-4">
        <CardBody className="flex flex-wrap items-center gap-3">
          <Switch
            checked={c.supportAccessAllowed}
            onChange={(v) => access.mutate(v)}
            disabled={access.isPending}
            label="Support access (read-only impersonation)"
            aria-label="Support access"
          />
          <span className="text-xs text-ink3">
            The owner controls this in Settings (W11). Turning it on here is audited on the company; prefer asking the owner.
          </span>
        </CardBody>
      </Card>

      <div className="two">
        <Card>
          <CardHeader title={`People (${c.users.length})`} />
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Active</th>
                <th>Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {c.users.length === 0 ? (
                <EmptyRow colSpan={5}>No people yet.</EmptyRow>
              ) : (
                c.users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-semibold">{u.name}</td>
                    <td>
                      <Pill tone={roleTone[u.role] ?? 'neutral'}>{u.role}</Pill>
                    </td>
                    <td className="text-ink2">{u.email ?? '—'}</td>
                    <td>{u.active ? 'Yes' : <span className="text-bad">No</span>}</td>
                    <td className="num text-ink2" title={u.lastSignInAt ?? ''}>
                      {u.role === 'worker' ? '—' : fmtAgo(u.lastSignInAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
        <Card>
          <CardHeader title={`Jobs (${c.jobs.length})`} />
          <Table>
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th className="r">Foremen</th>
                <th>Last entry</th>
              </tr>
            </thead>
            <tbody>
              {c.jobs.length === 0 ? (
                <EmptyRow colSpan={4}>No jobs yet.</EmptyRow>
              ) : (
                c.jobs.map((j) => (
                  <tr key={j.id}>
                    <td className="font-semibold">{j.name}</td>
                    <td>
                      <Pill tone={statusTone[j.status] ?? 'neutral'}>{j.status}</Pill>
                    </td>
                    <td className="r num">{j.foremanCount === 0 && j.status === 'active' ? <span className="text-bad">0</span> : j.foremanCount}</td>
                    <td className="num">{fmtDate(j.lastEntryDate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      </div>

      <div className="two mt-4">
        <Card>
          <CardHeader title="Recent sync logs" />
          <Table>
            <thead>
              <tr>
                <th>Kind</th>
                <th>Status</th>
                <th>Started</th>
                <th className="r">Sent</th>
                <th className="r">Failed</th>
              </tr>
            </thead>
            <tbody>
              {c.recentSyncLogs.length === 0 ? (
                <EmptyRow colSpan={5}>No QuickBooks runs yet.</EmptyRow>
              ) : (
                c.recentSyncLogs.map((l) => (
                  <tr key={l.id}>
                    <td className="font-semibold">{l.kind}</td>
                    <td>
                      <Pill tone={statusTone[l.status] ?? 'neutral'}>{l.status}</Pill>
                    </td>
                    <td className="num text-ink2">{fmtDateTime(l.startedAt)}</td>
                    <td className="r num">{l.recordsSent}</td>
                    <td className="r num">{l.recordsFailed ? <span className="text-bad">{l.recordsFailed}</span> : 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
        <Card>
          <CardHeader title="Recent audit events" />
          <Table>
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Actor</th>
              </tr>
            </thead>
            <tbody>
              {c.recentAuditEvents.length === 0 ? (
                <EmptyRow colSpan={4}>Nothing logged yet.</EmptyRow>
              ) : (
                c.recentAuditEvents.map((a) => (
                  <tr key={a.id} className={a.action.startsWith('support.') ? 'hi' : undefined}>
                    <td className="num text-ink2">{fmtDateTime(a.at)}</td>
                    <td className="font-semibold">{a.action}</td>
                    <td>
                      {a.entity}
                      {a.entityId ? <span className="sub">{a.entityId.slice(0, 8)}…</span> : null}
                    </td>
                    <td className="text-ink2">{a.actorName ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      </div>
    </>
  );
}
